import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "sonner";
import { searchParents } from "../../api/parents";
import { assignStudentParent } from "../../api/studentParent";
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";

type Props = {
  show: boolean;
  studentId: string;
  existingParentIds: string[];
  linkTypes: any[];
  onClose: () => void;
  onAssigned: (parent: any) => void;
};

const initialParent = {
  firstname: "",
  lastname: "",
  sexe: "Masculin" as "Masculin" | "Feminin",
  email: "",
  number: "",
  adresse: "",
  isDeceased: false,
  statut: true,
};

const displayName = (parent: any) =>
  `${parent?.firstname || ""} ${parent?.lastname || ""}`.trim();

const StudentParentAssignmentModal = ({
  show,
  studentId,
  existingParentIds,
  linkTypes,
  onClose,
  onAssigned,
}: Props) => {
  const [mode, setMode] = useState<"search" | "create">("search");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [linkId, setLinkId] = useState("");
  const [newParent, setNewParent] = useState({ ...initialParent });
  const [duplicates, setDuplicates] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const availableResults = useMemo(
    () => results.filter((parent) => !existingParentIds.includes(parent.id)),
    [results, existingParentIds],
  );

  const resetAndClose = () => {
    setMode("search");
    setQuery("");
    setResults([]);
    setLinkId("");
    setNewParent({ ...initialParent });
    setDuplicates([]);
    onClose();
  };

  useEffect(() => {
    if (!show || mode !== "search" || query.trim().length < 2) {
      setResults([]);
      return;
    }
    const timeout = window.setTimeout(async () => {
      try {
        setIsLoading(true);
        const response = await searchParents(
          { q: query.trim(), page: 1, limit: 10 },
          buildPermissionContext(Resource.STUDENT, Action.UPDATE),
        );
        setResults(response.data || []);
      } catch (error: any) {
        toast.error(error.response?.data?.message || "Recherche de parent impossible");
      } finally {
        setIsLoading(false);
      }
    }, 400);
    return () => window.clearTimeout(timeout);
  }, [query, mode, show]);

  useEffect(() => {
    if (!show || mode !== "create") return;
    const email = newParent.email.trim();
    const number = newParent.number.trim();
    if (!email && number.replace(/\D/g, "").length < 6) {
      setDuplicates([]);
      return;
    }
    const timeout = window.setTimeout(async () => {
      try {
        setIsLoading(true);
        const response = await searchParents(
          { email: email || undefined, number: number || undefined, limit: 5 },
          buildPermissionContext(Resource.STUDENT, Action.UPDATE),
        );
        setDuplicates(response.data || []);
      } catch {
        setDuplicates([]);
      } finally {
        setIsLoading(false);
      }
    }, 500);
    return () => window.clearTimeout(timeout);
  }, [newParent.email, newParent.number, mode, show]);

  const assign = async (parent?: any) => {
    if (!linkId) {
      toast.error("Choisissez la relation avec l'étudiant");
      return;
    }
    if (!parent && (!newParent.firstname.trim() || !newParent.lastname.trim())) {
      toast.error("Le nom et le prénom du parent sont obligatoires");
      return;
    }
    if (!parent && newParent.email && !/^\S+@\S+\.\S+$/.test(newParent.email)) {
      toast.error("L'adresse e-mail du parent est invalide");
      return;
    }
    if (!parent && duplicates.length > 0) {
      toast.error("Associez la fiche existante proposée.");
      return;
    }

    try {
      setIsLoading(true);
      const response = await assignStudentParent(
        {
          id_student: studentId,
          id_parent: parent?.id,
          id_link: linkId,
          newParent: parent
            ? undefined
            : {
                ...newParent,
                firstname: newParent.firstname.trim(),
                lastname: newParent.lastname.trim(),
                email: newParent.email.trim() || undefined,
                number: newParent.number.trim() || undefined,
                adresse: newParent.adresse.trim() || undefined,
              },
        },
        buildPermissionContext(Resource.STUDENT, Action.UPDATE),
      );
      onAssigned(response.data);
      toast.success("Parent associé avec succès");
      resetAndClose();
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || "Association impossible");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={resetAndClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Ajouter un parent ou responsable</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="btn-group mb-3" role="group" aria-label="Mode d'ajout">
          <button type="button" className={`btn ${mode === "search" ? "btn-primary" : "btn-light"}`} onClick={() => setMode("search")}>Rechercher</button>
          <button type="button" className={`btn ${mode === "create" ? "btn-primary" : "btn-light"}`} onClick={() => setMode("create")}>Créer un parent</button>
        </div>

        <div className="mb-3">
          <label className="form-label">Relation avec l'étudiant *</label>
          <select className="form-select" value={linkId} onChange={(event) => setLinkId(event.target.value)}>
            <option value="">Choisir une relation</option>
            {(linkTypes || []).map((link) => <option key={link.id} value={link.id}>{link.type}</option>)}
          </select>
        </div>

        {mode === "search" ? (
          <>
            <label className="form-label">Nom, téléphone ou e-mail</label>
            <input className="form-control" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher un parent existant" autoFocus />
            <div className="mt-3">
              {isLoading && <p className="text-muted">Recherche...</p>}
              {!isLoading && query.trim().length >= 2 && availableResults.length === 0 && <p className="text-muted">Aucun nouveau parent trouvé.</p>}
              {availableResults.map((parent) => (
                <div key={parent.id} className="border-bottom py-3 d-flex align-items-center justify-content-between gap-3">
                  <div><div className="fw-semibold">{displayName(parent)}</div><div className="text-muted fs-12">{parent.number || "Sans téléphone"}{parent.email ? ` · ${parent.email}` : ""}</div></div>
                  <button type="button" className="btn btn-outline-primary btn-sm" disabled={isLoading} onClick={() => assign(parent)}>Associer</button>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            {duplicates.length > 0 && (
              <div className="alert alert-warning">
                <div className="fw-semibold mb-2">Une fiche similaire existe déjà.</div>
                {duplicates.map((parent) => (
                  <div key={parent.id} className="d-flex justify-content-between align-items-center gap-2 py-1">
                    <span>{displayName(parent)} · {parent.number || parent.email}</span>
                    {existingParentIds.includes(parent.id) ? <span className="badge bg-secondary">Déjà associé</span> : <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => assign(parent)}>Associer</button>}
                  </div>
                ))}
              </div>
            )}
            <div className="row">
              <div className="col-md-6 mb-3"><label className="form-label">Prénom *</label><input className="form-control" value={newParent.firstname} onChange={(e) => setNewParent({ ...newParent, firstname: e.target.value })} /></div>
              <div className="col-md-6 mb-3"><label className="form-label">Nom *</label><input className="form-control" value={newParent.lastname} onChange={(e) => setNewParent({ ...newParent, lastname: e.target.value })} /></div>
              <div className="col-md-6 mb-3"><label className="form-label">Sexe *</label><select className="form-select" value={newParent.sexe} onChange={(e) => setNewParent({ ...newParent, sexe: e.target.value as "Masculin" | "Feminin" })}><option value="Masculin">Masculin</option><option value="Feminin">Féminin</option></select></div>
              <div className="col-md-6 mb-3"><label className="form-label">Téléphone</label><input className="form-control" value={newParent.number} onChange={(e) => setNewParent({ ...newParent, number: e.target.value })} /></div>
              <div className="col-md-6 mb-3"><label className="form-label">E-mail</label><input type="email" className="form-control" value={newParent.email} onChange={(e) => setNewParent({ ...newParent, email: e.target.value })} /></div>
              <div className="col-md-6 mb-3"><label className="form-label">Adresse</label><input className="form-control" value={newParent.adresse} onChange={(e) => setNewParent({ ...newParent, adresse: e.target.value })} /></div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-light" onClick={resetAndClose}>Annuler</button>
        {mode === "create" && <button type="button" className="btn btn-primary" disabled={isLoading || duplicates.length > 0} onClick={() => assign()}>Créer et associer</button>}
      </Modal.Footer>
    </Modal>
  );
};

export default StudentParentAssignmentModal;
